import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * LiveGiftFlow — rising-gift animation visible to every viewer + broadcaster.
 * During a PK battle, gifts are centered inside the recipient's window
 * (left or right half) instead of the gap between windows.
 *
 * Props:
 *   gifts        — list of gift records (newest first).
 *   battle       — current active battle (optional). When present, gift records
 *                  whose `to_user_id` matches battle.host_user_id animate in the
 *                  left half (if `mySide==="host"`) or right half (otherwise);
 *                  opponent gifts mirror.
 *   mySide       — "host" | "opponent" — which side of the battle the current
 *                  viewer / broadcaster is on. Determines which window is left.
 */
function laneFor(g, battle, mySide) {
  if (!battle || battle.status !== "active") return "full";
  const hostLeft = mySide === "host";
  const isForHost = g.to_user_id === battle.host_user_id;
  return (isForHost ? hostLeft : !hostLeft) ? "left" : "right";
}

// Pin only a battle lane. If the battle poll has not landed yet when a gift
// arrives, leave it unpinned so it moves into the correct window instead of
// being locked in the centre gap.
function pinLane(g, battle, mySide) {
  const lane = laneFor(g, battle, mySide);
  return lane === "full" ? null : lane;
}

export default function LiveGiftFlow({ gifts = [], battle = null, mySide = null }) {
  const [live, setLive] = useState([]);
  const seenRef = useRef(new Set());
  const seededRef = useRef(false);
  const battleRef = useRef(battle);
  const mySideRef = useRef(mySide);
  battleRef.current = battle;
  mySideRef.current = mySide;

  useEffect(() => {
    if (!gifts || gifts.length === 0) return;
    if (!seededRef.current) {
      gifts.forEach((g) => seenRef.current.add(g.id));
      seededRef.current = true;
      return;
    }
    const fresh = [];
    for (const g of gifts) {
      if (!seenRef.current.has(g.id)) {
        seenRef.current.add(g.id);
        fresh.push(g);
      }
    }
    if (fresh.length === 0) return;
    const stamp = Date.now();
    setLive((prev) => [
      ...prev,
      ...fresh.slice(0, 5).map((g, i) => ({ key: `${g.id}-${stamp}-${i}`, gift: g, lane: pinLane(g, battleRef.current, mySideRef.current) })),
    ]);
  }, [gifts]);

  const retire = (key) => setLive((prev) => prev.filter((x) => x.key !== key));

  // During a battle, decide which half each gift animates in. `host` on left
  // (if I'm host), `opponent` on right.
  const laneForGift = (g) => laneFor(g, battle, mySide);

  return (
    <div className="pointer-events-none absolute inset-0 z-[25] overflow-hidden" data-testid="live-gift-flow">
      <AnimatePresence>
        {live.map(({ key, gift, lane: pinnedLane }) => {
          const lane = pinnedLane || laneForGift(gift);
          let leftPct = 50;
          if (lane === "left") leftPct = 25;
          else if (lane === "right") leftPct = 75;
          return (
            <motion.div
              key={key}
              initial={{ bottom: "-10%", opacity: 0, scale: 0.4, rotate: -8 }}
              animate={{
                bottom: ["-10%", "35%", "45%"],
                opacity: [0, 1, 1, 0],
                scale: [0.4, 1.6, 1.9, 1.9],
                rotate: [-8, 4, 0],
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.4, times: [0, 0.35, 0.75, 1], ease: "easeOut" }}
              onAnimationComplete={() => retire(key)}
              style={{ left: `${leftPct}%`, translateX: "-50%" }}
              className="absolute will-change-transform flex flex-col items-center"
            >
              <div className="relative">
                <div className="absolute inset-0 blur-2xl rounded-full" style={{
                  background: "radial-gradient(circle, rgba(255,170,0,0.75), rgba(255,42,95,0.4) 60%, transparent 72%)",
                }}/>
                <div className="relative text-[72px] leading-none drop-shadow-[0_0_18px_rgba(255,180,30,0.95)]" aria-hidden>
                  {gift.gift_emoji || "🎁"}
                </div>
              </div>
              <div className="mt-1 px-2 py-0.5 rounded-full bg-black/55 text-[10px] font-extrabold text-white backdrop-blur">
                @{gift.from_username || "someone"} · <span className="text-[#FF7A00]">{gift.gift_name || "Gift"}</span>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
